import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ThemeColors {
	primaryColor: string;
	secondaryColor: string;
	surfaceColor?: string;
	surfaceLight?: string;
	surfaceDark?: string;
	primaryDark?: string;
	secondaryLight?: string;
	outlineMain?: string;
	outlineDark?: string;
	textMainDark2?: string;
	textMainMain2?: string;
	textSecondaryMain?: string;
	tertiaryColor?: string;
	surfaceTint?: string;
	successLight?: string;
	successDark?: string;
	brandFaint?: string;
	dividerColor?: string;
	surfaceTwo?: string;
	textPrimaryMain?: string;
	fontFamily?: string;
}

export interface ThemeStore {
	primaryColor: string | null;
	secondaryColor: string | null;
	surfaceColor: string | null;
	surfaceLight: string | null;
	surfaceDark: string | null;
	primaryDark: string | null;
	secondaryLight: string | null;
	outlineMain: string | null;
	outlineDark: string | null;
	textMainDark2: string | null;
	textMainMain2: string | null;
	textSecondaryMain: string | null;
	tertiaryColor: string | null;
	surfaceTint: string | null;
	successLight: string | null;
	successDark: string | null;
	brandFaint: string | null;
	dividerColor: string | null;
	surfaceTwo: string | null;
	textPrimaryMain: string | null;
	fontFamily: string | null;
	isLoaded: boolean;
	_hasHydrated: boolean;
	setThemeColors: (colors: ThemeColors) => void;
	clearThemeColors: () => void;
	setHasHydrated: (state: boolean) => void;
}

export const useThemeStore = create<ThemeStore>()(
	persist(
		(set) => ({
			primaryColor: null,
			secondaryColor: null,
			surfaceColor: null,
			surfaceLight: null,
			surfaceDark: null,
			primaryDark: null,
			secondaryLight: null,
			outlineMain: null,
			outlineDark: null,
			textMainDark2: null,
			textMainMain2: null,
			textSecondaryMain: null,
			tertiaryColor: null,
			surfaceTint: null,
			successLight: null,
			successDark: null,
			brandFaint: null,
			dividerColor: null,
			surfaceTwo: null,
			textPrimaryMain: null,
			fontFamily: null,
			isLoaded: false,
			_hasHydrated: false,
			setThemeColors: (colors: ThemeColors) =>
				set({
					primaryColor: colors.primaryColor,
					secondaryColor: colors.secondaryColor,
					surfaceColor: colors.surfaceColor || null,
					surfaceLight: colors.surfaceLight || null,
					surfaceDark: colors.surfaceDark || null,
					primaryDark: colors.primaryDark || null,
					secondaryLight: colors.secondaryLight || null,
					outlineMain: colors.outlineMain || null,
					outlineDark: colors.outlineDark || null,
					textMainDark2: colors.textMainDark2 || null,
					textMainMain2: colors.textMainMain2 || null,
					textSecondaryMain: colors.textSecondaryMain || null,
					tertiaryColor: colors.tertiaryColor || null,
					surfaceTint: colors.surfaceTint || null,
					successLight: colors.successLight || null,
					successDark: colors.successDark || null,
					brandFaint: colors.brandFaint || null,
					dividerColor: colors.dividerColor || null,
					surfaceTwo: colors.surfaceTwo || null,
					textPrimaryMain: colors.textPrimaryMain || null,
					fontFamily: colors.fontFamily || null,
					isLoaded: true,
				}),
			clearThemeColors: () =>
				set({
					primaryColor: null,
					secondaryColor: null,
					surfaceColor: null,
					surfaceLight: null,
					surfaceDark: null,
					primaryDark: null,
					secondaryLight: null,
					outlineMain: null,
					outlineDark: null,
					textMainDark2: null,
					textMainMain2: null,
					textSecondaryMain: null,
					tertiaryColor: null,
					surfaceTint: null,
					successLight: null,
					successDark: null,
					brandFaint: null,
					dividerColor: null,
					surfaceTwo: null,
					textPrimaryMain: null,
					fontFamily: null,
					isLoaded: false,
				}),
			setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
		}),
		{
			name: "ThemeStore",
			onRehydrateStorage: () => (state) => {
				state?.setHasHydrated(true);
			},
			// What survives a page load. This is a FIFTH hand-written mapping on top of the
			// four that themeSettingsChain.test.ts pins, and the one that runs on every
			// reload: a role absent here is fetched, used for one render, then dropped.
			//
			// It also cannot simply be skipped in favour of the API, because `isLoaded` is
			// persisted too — provider.tsx treats a cached `isLoaded: true` as "render now",
			// so an incomplete cache is rendered rather than waited on, and App.tsx's
			// settings call is explicitly best-effort (a failure is swallowed).
			//
			// The six SIMS-8458 roles were missing here until SIMS-8592. Because each one
			// falls back to a DEFAULT_* constant taken from the JANUS tenant, a
			// purple-branded tenant reloaded into green accents (brandFaint #5AAB96,
			// divider/surfaceTwo #D2DCDF) rather than into a subtly wrong purple.
			partialize: (state) => ({
				primaryColor: state.primaryColor,
				secondaryColor: state.secondaryColor,
				surfaceColor: state.surfaceColor,
				surfaceLight: state.surfaceLight,
				surfaceDark: state.surfaceDark,
				primaryDark: state.primaryDark,
				secondaryLight: state.secondaryLight,
				outlineMain: state.outlineMain,
				outlineDark: state.outlineDark,
				textMainDark2: state.textMainDark2,
				textMainMain2: state.textMainMain2,
				textSecondaryMain: state.textSecondaryMain,
				tertiaryColor: state.tertiaryColor,
				surfaceTint: state.surfaceTint,
				successLight: state.successLight,
				successDark: state.successDark,
				brandFaint: state.brandFaint,
				dividerColor: state.dividerColor,
				surfaceTwo: state.surfaceTwo,
				textPrimaryMain: state.textPrimaryMain,
				fontFamily: state.fontFamily,
				isLoaded: state.isLoaded,
			}),
		},
	),
);
